/**
 * AP-38-I — Mandant aus der Betreiber-Konsole anlegen.
 *
 * Der Dialog fragt nur, was zum Anlegen nötig ist (AP-36 Fall 2). Wer anlegt,
 * wird mit seinem bestehenden Anmeldekonto Administrator des neuen Mandanten —
 * ein Feld für eine fremde Adresse gibt es bewusst nicht: Alle anderen kommen
 * über die Einladung aus dem Mandanten und nehmen selbst an.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@symphenia/ui-components'
import { Button } from '@/components/ui/button'
import { apiErrorText } from '@/lib/api-error'
import { fieldLabel, inputBase } from '@/lib/form-styles'
import { operatorApi } from '../api/operator.api'

const SUBDOMAIN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/** „Testbetrieb Lohn (Kanzlei Müller)" → `testbetrieb-lohn-kanzlei-mueller`. */
export function subdomainVorschlag(name: string): string {
  return name
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, 50)
    .replace(/^-+|-+$/g, '')
}

export function MandantAnlegen({ onClose, onFertig }: { onClose: () => void; onFertig: (mandant: { id: string }) => void }) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  // Solange niemand die Subdomain selbst angefasst hat, folgt sie dem Namen.
  const [eigeneSubdomain, setEigeneSubdomain] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const subdomain = eigeneSubdomain ?? subdomainVorschlag(name)
  const subdomainGueltig = subdomain.length >= 3 && subdomain.length <= 50 && SUBDOMAIN.test(subdomain)
  const bereit = name.trim().length >= 2 && subdomainGueltig && !laeuft

  const anlegen = async () => {
    setLaeuft(true)
    setFehler(null)
    try {
      onFertig(await operatorApi.createTenant({ tenantName: name.trim(), subdomain }))
    } catch (e) {
      setFehler(apiErrorText(e, t('common.error')))
      setLaeuft(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('operator.mandantAnlegen.titel')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button disabled={!bereit} onClick={() => void anlegen()}>{t('common.create')}</Button>
        </div>
      }
    >
      <div className="space-y-4 text-sm">
        <label className="block">
          <span className={fieldLabel}>{t('operator.mandantAnlegen.name')}</span>
          <input
            className={inputBase}
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('operator.mandantAnlegen.nameBeispiel')}
            autoFocus
          />
        </label>
        <label className="block">
          <span className={fieldLabel}>{t('operator.mandantAnlegen.subdomain')}</span>
          <input
            className={`${inputBase} font-mono`}
            value={subdomain}
            maxLength={50}
            onChange={(e) => setEigeneSubdomain(e.target.value)}
            aria-invalid={subdomain.length > 0 && !subdomainGueltig}
          />
          {subdomain.length > 0 && !subdomainGueltig && (
            <span className="mt-1 block text-xs text-destructive">{t('operator.mandantAnlegen.subdomainRegel')}</span>
          )}
        </label>
        <p className="text-muted-foreground">{t('operator.mandantAnlegen.hinweis')}</p>
        {fehler && <p className="text-destructive">{fehler}</p>}
      </div>
    </Modal>
  )
}
